import { Story } from '../../types/story';

export const amsterdamInBetween: Story = {
  id: 'amsterdam-in-between',
  title: 'In Between',
  excerpt: 'A chance encounter at a canal-side café leads to an afternoon that challenges everything they thought they knew about themselves.',
  city: 'Amsterdam',
  publishDate: '2025-12-21',
  readTime: '15 min read',
  tags: ['♂♀', 'discovery', 'afternoon', 'transformation'],
  categories: ['romantic'],
  intensity: 2,
  content: `The rain had started suddenly, as it often does in Amsterdam, sending tourists scrambling for cover under awnings and into cafés. I'd been sitting outside, sketching the canal, when the first drops fell. By the time I gathered my things, I was already soaked.

The café was crowded with other refugees from the rain. I found a small table near the window and ordered coffee, spreading out my damp sketchbook to assess the damage. Most of it was salvageable, though one drawing—a portrait I'd been working on—had run into an abstract blur.

"That's actually kind of beautiful," a voice said.

I looked up to find someone standing beside my table, holding their own cup of coffee. They had short hair, an androgynous style that made it impossible to immediately categorize them, and the most striking green eyes I'd ever seen.

"The way the water made the lines bleed," they continued, gesturing to my ruined sketch. "It's like the person is dissolving. Very existential."

I laughed. "That's a generous interpretation. I was just thinking I'd wasted two hours."

"Mind if I sit? Everywhere else is full."

"Please."

They sat down across from me, and I noticed their hands—long fingers, no rings, nails painted a deep burgundy. "I'm Alex."

"Morgan."

"Are you an artist, Morgan?"

"Trying to be. You?"

"Musician. Well, trying to be." They smiled. "I play guitar at a bar in Jordaan a few nights a week. Mostly covers, some original stuff. It pays the rent, barely."

We talked easily, the way you sometimes can with strangers. Alex told me about growing up in Rotterdam, about moving to Amsterdam for the music scene, about the constant struggle between making art and making money. I told them about my own journey—the graphic design job I'd quit six months ago, the savings that were rapidly dwindling, the fear that I'd made a terrible mistake.

"But you're doing it," Alex said. "You're actually doing the thing most people only dream about. That takes courage."

"Or stupidity."

"Maybe they're the same thing." They leaned forward, their eyes intense. "Can I tell you something? I saw you before the rain started. You were so focused on your drawing, completely lost in it. That's rare. Most people are always half-present, always thinking about something else. But you were entirely there."

I felt heat rise to my cheeks. "You were watching me?"

"I was admiring you. There's a difference." They paused. "Is that weird? I can leave if that's weird."

"It's not weird." And it wasn't. It was flattering, intriguing, and something else I couldn't quite name.

The rain continued, and we ordered more coffee, then lunch, then wine. The café began to empty as the afternoon wore on, but neither of us made a move to leave. We talked about art and music, about the books we loved, about our families and the ways they didn't quite understand us.

"Can I ask you something personal?" Alex said eventually.

"Sure."

"Are you attracted to me?"

The question was so direct it took my breath away. "I... yes. I think so."

"You think so?"

"I don't usually... I mean, I've only ever been with men. But yes. I'm attracted to you."

Alex smiled. "I'm non-binary. They/them pronouns. And I'm attracted to you too, in case that wasn't obvious."

"It wasn't. I mean, I hoped, but—"

"Want to get out of here?"

My heart was pounding. "Where would we go?"

"My place is a ten-minute walk. We could listen to music. Talk more. See what happens."

I should have been nervous, should have overthought it, should have made a list of all the reasons this was outside my comfort zone. Instead, I just said yes.

Alex's apartment was exactly what I would have expected—small and cluttered with instruments, books stacked everywhere, art on every wall. They put on a record—something jazzy and atmospheric—and we sat on the couch, closer than we'd been in the café.

"I want to kiss you," Alex said. "But I want to make sure you're comfortable with this."

"I'm comfortable. Nervous, but comfortable."

"We can go slow. And we can stop anytime."

When they kissed me, it was different from any kiss I'd experienced before. Softer, more exploratory, like they were learning me. Their hands stayed respectfully on my face, in my hair, not pushing for more.

"Okay?" they murmured against my lips.

"More than okay."

We moved slowly, checking in with each other at every step. When their hand slid under my shirt, they paused, waiting for my nod before continuing. They were patient and attentive, and I found myself relaxing into it, into them, into this new experience.

"You're beautiful," they whispered, and I believed them.

We made our way to the bedroom, shedding clothes between kisses. Alex's body was a revelation—curves and angles, soft and strong, defying easy categorization. They showed me how to touch them, what they liked, and I discovered I liked learning, liked the way they responded to my tentative explorations.

When they touched me, it was with a confidence that made me melt. They seemed to know exactly what I needed, building the pleasure slowly until I was gasping their name.

"Let go," they whispered. "I've got you."

And I did. I let go of my expectations, my categories, my need to define and understand everything. I just felt—their hands on my skin, their mouth on mine, the way our bodies moved together in a rhythm that felt both foreign and completely natural.

Afterward, we lay tangled together, the record long since finished, the room filled with the sound of rain against the windows.

"That was..." I started, but couldn't find the words.

"Yeah," Alex agreed, understanding without explanation.

"I don't know what this means. About me, I mean."

"It doesn't have to mean anything. Or it can mean everything. That's up to you." They propped themselves up on one elbow, looking down at me. "But for what it's worth, I think you're someone who feels deeply and loves openly. The rest is just details."

I stayed until evening, and we made love again as the city lights began to glow outside. When I finally left, Alex walked me to the door and kissed me goodbye.

"Will I see you again?" I asked.

"Do you want to?"

"Yes."

"Then you will." They wrote their number on my palm with a pen. "Call me. Or don't. But I hope you do."

I did call, three days later. And then again. And again. What started as a rainy afternoon became something more—not a relationship, exactly, but a connection that changed the way I understood myself and what I wanted.

Sometimes the most important people in our lives are the ones who show us we're capable of more than we imagined.`
};
